import { createRootRoute, Outlet, redirect, useLocation, useMatches } from "@tanstack/react-router"
import { Suspense, lazy, useEffect } from "react"
import { useQuery } from "@tanstack/react-query"
import { Providers } from "@/components/providers"
import { LoginScreen } from "@/components/login-screen"
import { WorkspaceLogin } from "@/components/workspace-login"
import { queryClient } from "../query-client"
import { meQueryOptions } from "../me"
import { getAppRouter } from "../router-ref"
import { trackRoute } from "../track/browser"

const SALE_WORKSPACE_HOST = "saleworkspace.vucar.vn"

// Gọi audio nội bộ (getstream.io): SDK nặng — tải lười sau khi đăng nhập, không chặn lần vẽ đầu.
const AudioCallLayer = lazy(() => import("@/components/calls/AudioCallLayer"))
// Chat ở góc kiểu Messenger: gắn ở gốc để đi trang nào khung chat cũng không tắt (Thinh 09/10).
const ChatDock = lazy(() => import("@/components/kanban/ChatDock"))

// Every route is auto-code-split; a cold import leaves a blank gap on first
// navigation (the old page unmounts before the new chunk arrives). Warm all
// chunks in idle time after first paint so nav swaps are synchronous.
const WARMUP_PATHS = [
  "/agent-performance", "/agent-playground", "/agent-queue", "/agent-queue/overview",
  "/agents", "/agents/followups", "/agents/manager", "/agents/tasks", "/agents/tasks/$picId",
  "/ai-funnel", "/ai-performance", "/booking-link-logs", "/car-dealer-match", "/dead-leads",
  "/dealer-delivery", "/dealer-performance", "/dealer-tasks", "/decoy-management",
  "/e2e/$userId", "/e2e/$userId/ai-performance", "/e2e/$userId/crm-bi", "/e2e/$userId/cx-qc",
  "/e2e/$userId/dead-leads", "/e2e/$userId/dealers", "/e2e/$userId/funnel",
  "/e2e/$userId/lead/$leadId", "/e2e/$userId/tasks", "/e2e/$userId/tasks/$view",
  "/eval-testing", "/inspection-rerun", "/inspector", "/lead-monitor", "/new-car-coverage",
  "/persona-management/$picId", "/persona-management/$picId/runs/$carId", "/pic-label", "/pic-label/analytics", "/pic-personas", "/scheduler",
  "/stage-outcome-canary", "/watcher/$picId/$carId", "/win-chat-analytics",
  "/workflow-management", "/workspace",
]

function warmRoutes(userId: string) {
  const ric = window.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 800))
  ric(() => {
    const router = getAppRouter()
    if (!router) return
    for (const pattern of WARMUP_PATHS) {
      const to = pattern
        .replaceAll("$userId", userId)
        .replaceAll("$picId", userId)
        .replaceAll("$leadId", "warmup")
        .replaceAll("$carId", "warmup")
        .replaceAll("$view", "board")
      try {
        void router.preloadRoute({ to } as never).catch(() => {})
      } catch {
        // pattern didn't resolve — chunk warmup is best-effort
      }
    }
  })
}

export const Route = createRootRoute({
  // Next had a beforeFiles rewrite: saleworkspace.vucar.vn/ → /workspace.
  // Same effect here, client-side — the URL does change to /workspace
  // (Next kept it at "/"; harmless, arguably clearer).
  beforeLoad: () => {
    if (
      typeof window !== "undefined" &&
      window.location.hostname === SALE_WORKSPACE_HOST &&
      window.location.pathname === "/"
    ) {
      throw redirect({ to: "/workspace" })
    }
  },
  component: RootComponent,
  notFoundComponent: () => (
    <div className="flex min-h-screen items-center justify-center text-muted-foreground">
      404 — trang không tồn tại
    </div>
  ),
})

function RootComponent() {
  const me = useQuery(meQueryOptions)
  const pathname = useLocation({ select: (l) => l.pathname })
  const userId = me.data?.id
  useEffect(() => {
    if (userId) warmRoutes(userId)
  }, [userId])
  // Đo thời gian ở mỗi màn → HAE (qua /api/e2e/hae-track). Key theo route TEMPLATE + params uuid.
  const leaf = useMatches({ select: (ms) => ms[ms.length - 1] })
  const leafId = leaf?.routeId ?? null
  const leafParams = JSON.stringify(leaf?.params ?? {})
  useEffect(() => {
    if (!userId) return trackRoute(null, {}, null)
    trackRoute(leafId, JSON.parse(leafParams), userId)
  }, [userId, leafId, leafParams])
  const isSaleWorkspace = window.location.hostname === SALE_WORKSPACE_HOST
  // /pic-personas is the lightweight registry surface (no Accounts/Orchestrator).
  const lightweight = pathname.startsWith("/pic-personas")

  if (me.isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground">Đang tải…</div>
      </div>
    )
  }

  if (!me.data) {
    return isSaleWorkspace ? <WorkspaceLogin /> : <LoginScreen />
  }

  return (
    <Providers user={me.data} lightweight={lightweight} queryClient={queryClient}>
      <Outlet />
      {!lightweight && !isSaleWorkspace && (
        <Suspense fallback={null}>
          <AudioCallLayer userId={me.data.id} />
        </Suspense>
      )}
      {!lightweight && !isSaleWorkspace && (
        <Suspense fallback={null}>
          <ChatDock userId={me.data.id} />
        </Suspense>
      )}
    </Providers>
  )
}
